import { DataType, Float64, Int64, Utf8, Bool, DateDay, TimestampMillisecond } from 'apache-arrow';
import { TableDataSource, TableSourceType, TableFormat } from '../core/types';

/**
 * Map DuckDB types to Apache Arrow DataType objects
 * This is a pure function with no dependencies on DuckDB instances
 */
export function mapDuckDBTypeToColumnType(params: { duckDBType: string }): DataType {
  const { duckDBType } = params;
  const type = duckDBType.toUpperCase();
  
  if (type.includes('VARCHAR') || type.includes('TEXT')) {
    return new Utf8();
  } else if (type.includes('INTEGER') || type.includes('BIGINT') || type.includes('INT32') || type.includes('INT64')) {
    return new Int64();
  } else if (type.includes('DOUBLE') || type.includes('FLOAT') || type.includes('DECIMAL') || type.includes('NUMERIC')) {
    return new Float64();
  } else if (type.includes('BOOLEAN')) {
    return new Bool();
  } else if (type.includes('DATE')) {
    return new DateDay();
  } else if (type.includes('TIMESTAMP')) {
    return new TimestampMillisecond();
  } else if (type.includes('TIME')) {
    return new TimestampMillisecond(); // Using TimestampMillisecond for TIME as well since Arrow doesn't have a dedicated TIME type in WASM
  }

  return new Utf8();
}

/**
 * Generate a unique table ID based on the source type
 * This is a pure function that generates predictable IDs
 */
export function generateTableId(params: { source: TableDataSource }): string {
  const { source } = params;
  const uuid = crypto.randomUUID();
  
  switch (source.type) {
    case TableSourceType.LOCAL_FILE:
      return `local-${uuid}`;
    case TableSourceType.URL:
      return `url-${uuid}`;
    case TableSourceType.DATATABLE:
      return `datatable-${uuid}`;
    default:
      return uuid;
  }
}

/**
 * Generate a default path for a table based on its source
 * This is a pure function that creates human-readable paths
 */
export function getDefaultPath(params: { source: TableDataSource; id: string }): string {
  const { source, id } = params;
  
  switch (source.type) {
    case TableSourceType.LOCAL_FILE:
      // For files, keep the extension and ensure path starts with /
      const fileName = source.fileId;
      return fileName.startsWith('/') ? fileName : `/${fileName}`;
    
    case TableSourceType.URL:
    case TableSourceType.DATATABLE:
      try {
        const url = new URL(source.url);
        // Extract path from URL and ensure it starts with /
        // For URLs, keep the extension if it's a file path
        const pathParts = url.pathname.split('/').filter(Boolean);
        return pathParts.length > 0 ? `/${pathParts.join('/')}` : `/url_${id}`;
      } catch {
        // If URL parsing fails, create a fallback path without extension
        return `/url_${id}`;
      }
    
    case TableSourceType.MATERIALIZED:
      // For materialized tables, create a path without extension
      return `/table_${id}`;
  }
}

/**
 * Clean a file name to make it suitable for use as a DuckDB identifier
 * This is a pure function that sanitizes file names
 */
export function cleanFileName(params: { filename: string }): string {
  const { filename } = params;
  
  return filename
    // Remove file extension (everything after the last dot)
    .replace(/\.[^/.]+$/, '')
    // Replace remaining dots with underscores (for files like file.tar.gz)
    .replace(/\./g, '_')
    // Replace spaces and special characters with single underscore
    .replace(/[ ()-]+/g, '_')
    // Remove any remaining non-alphanumeric characters except underscores
    .replace(/[^a-zA-Z0-9_]/g, '')
    // Remove leading and trailing underscores
    .replace(/^_+|_+$/g, '')
    // Replace multiple consecutive underscores with single underscore
    .replace(/_+/g, '_');
}

/**
 * Determine the table format based on file extension
 * This is a pure function that maps extensions to formats
 */
export function getTableFormatFromExtension(params: { filename: string }): TableFormat {
  const { filename } = params;
  const extension = filename.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'json':
      return TableFormat.JSON;
    case 'parquet':
      return TableFormat.PARQUET;
    case 'arrow':
      return TableFormat.ARROW;
    case 'tsv':
      return TableFormat.TSV;
    default:
      return TableFormat.CSV;
  }
}

/**
 * Validate that a table schema is valid
 * This is a pure function that checks schema structure
 */
export function validateTableSchema(params: { schema: { columns?: unknown[] } }): void {
  const { schema } = params;
  
  if (!schema || !schema.columns || schema.columns.length === 0) {
    throw new Error('Invalid table schema');
  }
} 